import cc from 'classcat';
import { ChangeEvent, FocusEventHandler, KeyboardEventHandler, MouseEventHandler, useState } from 'react';
import { Label } from '../Label';

type BorderStyle = 'dotted' | 'dashed' | 'solid';

interface InputProps {
    id: string;
    value: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onClick?: MouseEventHandler<HTMLInputElement>;
    onFocus?: FocusEventHandler<HTMLInputElement>;
    type?: string;
    placeholder?: string;
    label?: string;
    className?: string;
    maxLength?: number;
    icon?: string;
    iconClassName?: string;
    iconAlt?: string;
    onIconClick?: MouseEventHandler<HTMLImageElement>;
    autoComplete?: 'on' | 'off';
    isError?: boolean;
    errorMessage?: string;
    disabled?: boolean;
    borderType?: BorderStyle;
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
    isRequired?: boolean;
}

export const Input = ({
    id,
    type,
    placeholder,
    label,
    onChange,
    onClick,
    onFocus,
    className,
    maxLength,
    value,
    icon,
    iconClassName,
    iconAlt,
    onIconClick,
    autoComplete,
    isError,
    errorMessage,
    disabled,
    borderType,
    onKeyDown,
    isRequired,
}: InputProps) => {
    const [isFocused, setIsFocused] = useState<boolean>(false);

    return (
        <div className={cc(['input-style', className])}>
            {label && <Label className="input-style__label paragraph-4" isRequired={isRequired} label={label} />}
            <input
                className={cc([
                    'input-style__field',
                    { 'input-style__field--error': isError },
                    { 'input-style__field--border-dashed': borderType === 'dashed' },
                ])}
                id={id}
                name={id}
                value={value}
                type={type ?? 'text'}
                placeholder={placeholder}
                onChange={onChange}
                onBlur={() => setIsFocused(false)}
                onClick={onClick}
                onFocus={onFocus ?? (() => setIsFocused(true))}
                maxLength={maxLength}
                autoComplete={autoComplete ?? 'off'}
                disabled={disabled}
                onKeyDown={onKeyDown}
            />

            {icon && (
                <img
                    className={cc(['input-style__icon-button', iconClassName, disabled && 'input-style__icon-button--disabled'])}
                    src={icon}
                    onClick={onIconClick}
                    alt={iconAlt ?? 'Icon'}
                />
            )}

            {isError && isFocused && <span className={cc(['input-style__error-msg'])}>{errorMessage}</span>}
        </div>
    );
};
